import { InboundAudioFields, InboundVideoFields, StatsGraph } from 'utils/webrtc/stats/types';
import { FeedId, SubscriberMid } from 'features/streaming/types';
import { ReceivingFieldsState, ReceivingMidFields } from 'features/system-stats/types';

const EMPTY_RESULT: ReceivingFieldsState = { byMid: {}, totalBandwidthBps: 0 };

// trackKey shape: <pcKey>::<mid>::<rid>
const midFromTrackKey = (trackKey: string): string | undefined => {
  const parts = trackKey.split('::');
  return parts.length >= 3 ? parts[1] : undefined;
};

type SimulcastAccumulator = {
  feedId: FeedId;
  mid: SubscriberMid;
  kind: 'audio' | 'video';
  bitrateBps: number;
  framesPerSecond?: number;
  frameWidth?: number;
  frameHeight?: number;
  codec?: string;
};

const accumulate = (
  acc: Map<SubscriberMid, SimulcastAccumulator>,
  mid: SubscriberMid,
  feedId: FeedId,
  kind: 'audio' | 'video',
  fields: InboundAudioFields | InboundVideoFields
): void => {
  const bitrate = fields.bitrate ?? 0;
  const existing = acc.get(mid);

  if (!existing) {
    // fps without a resolution can't feed the highest-res-rid tie-break below,
    // so only record fps when frameWidth is present.
    let framesPerSecond: number | undefined;
    let frameWidth: number | undefined;
    let frameHeight: number | undefined;
    let codec: string | undefined;
    if (kind === 'video') {
      const v = fields as InboundVideoFields;
      codec = v.codec;
      if (v.frameWidth !== undefined) {
        framesPerSecond = v.framesPerSecond;
        frameWidth = v.frameWidth;
        frameHeight = v.frameHeight;
      }
    }
    acc.set(mid, {
      feedId,
      mid,
      kind,
      bitrateBps: bitrate,
      framesPerSecond,
      frameWidth,
      frameHeight,
      codec,
    });
    return;
  }

  // Simulcast: accumulate bitrate; pick fps from the highest-resolution rid.
  existing.bitrateBps += bitrate;
  if (kind === 'video') {
    const v = fields as InboundVideoFields;
    if (v.frameWidth !== undefined) {
      if (existing.frameWidth === undefined || v.frameWidth > existing.frameWidth) {
        existing.framesPerSecond = v.framesPerSecond;
        existing.frameWidth = v.frameWidth;
        existing.frameHeight = v.frameHeight;
      }
    }
    if (existing.codec === undefined) {
      existing.codec = v.codec;
    }
  }
};

export function extractReceivingFields(
  receiverGraph: StatsGraph | null,
  feedIdByMid: Record<SubscriberMid, FeedId>
): ReceivingFieldsState {
  if (receiverGraph === null) {
    return EMPTY_RESULT;
  }

  const acc = new Map<SubscriberMid, SimulcastAccumulator>();

  receiverGraph.inboundAudio.forEach((fields, trackKey) => {
    const mid = midFromTrackKey(trackKey);
    if (mid === undefined) {
      return;
    }
    const feedId = feedIdByMid[mid];
    if (feedId === undefined) {
      return;
    }
    accumulate(acc, mid, feedId, 'audio', fields);
  });

  receiverGraph.inboundVideo.forEach((fields, trackKey) => {
    const mid = midFromTrackKey(trackKey);
    if (mid === undefined) {
      return;
    }
    const feedId = feedIdByMid[mid];
    if (feedId === undefined) {
      return;
    }
    accumulate(acc, mid, feedId, 'video', fields);
  });

  if (acc.size === 0) {
    return EMPTY_RESULT;
  }

  const byMid: Record<SubscriberMid, ReceivingMidFields> = {};
  let totalBandwidthBps = 0;

  acc.forEach((row) => {
    const { feedId, mid, kind, bitrateBps, framesPerSecond, frameWidth, frameHeight, codec } = row;
    byMid[mid] = { feedId, mid, kind, bitrateBps, framesPerSecond, frameWidth, frameHeight, codec };
    totalBandwidthBps += bitrateBps;
  });

  return { byMid, totalBandwidthBps };
}
